import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Upload, X, Check, Clock, UserPlus, Search } from "lucide-react";
import api, { formatError } from "@/lib/api";

const PLANS = { one: { label: "1 subject", n: 1, price: 200 }, three: { label: "3 subjects", n: 3, price: 500 }, all: { label: "All subjects", n: 5, price: 800 } };
import { gradeSubjects } from "@/lib/subjects";
const inp = "bg-slate-900 border border-slate-600 rounded-lg px-2 py-1.5 text-sm";
const READING_PRICE = 200;
const READING_OK = (plan) => !plan || plan === "one";
const nextMonth = () => { const d = new Date(); d.setMonth(d.getMonth() + 1); return d.toISOString().slice(0, 10); };

export default function ManualPayment({ onDone }) {
  const [open, setOpen] = useState(false);
  const [parent, setParent] = useState(null);
  const close = () => { setOpen(false); setParent(null); };
  if (!open) return (
    <button onClick={() => setOpen(true)} data-testid="manual-payment-open"
      className="inline-flex items-center gap-1.5 rounded-full bg-yellow-300 text-slate-900 px-4 py-2 text-sm font-bold">
      <Plus size={15} /> Add a payment for a parent / Ajouter un paiement
    </button>
  );
  return (
    <div className="bg-slate-800 rounded-2xl border-2 border-yellow-300/50 p-4 space-y-4" data-testid="manual-payment">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-lg">Add a payment for a parent</h3>
        <button onClick={close} data-testid="manual-payment-close"><X size={18} /></button>
      </div>
      {!parent ? <PickParent onPick={setParent} /> : (
        <>
          <p className="text-sm" data-testid="manual-payment-parent">Parent: <b>{parent.name}</b> · {parent.email}
            <button onClick={() => setParent(null)} className="ml-2 text-xs text-yellow-300 underline" data-testid="manual-payment-change-parent">change</button></p>
          <PaymentForm parent={parent} onDone={() => { close(); onDone(); }} />
        </>
      )}
    </div>
  );
}

function PickParent({ onPick }) {
  const [mode, setMode] = useState("existing");
  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        {[["existing", "Existing parent", Search], ["new", "New parent", UserPlus]].map(([m, l, I]) => (
          <button key={m} onClick={() => setMode(m)} data-testid={`manual-payment-mode-${m}`}
            className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full border-2 text-sm font-semibold ${mode === m ? "bg-yellow-300 text-slate-900 border-yellow-300" : "border-slate-600"}`}><I size={14} /> {l}</button>
        ))}
      </div>
      {mode === "existing" ? <ExistingParent onPick={onPick} /> : <NewParent onPick={onPick} />}
    </div>
  );
}

function ExistingParent({ onPick }) {
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const load = () => list || api.get("/admin/parents").then(({ data }) => setList(data.parents)).catch(() => setList([]));
  const rows = (list || []).filter((p) => q.trim().length > 1 && `${p.name} ${p.email}`.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 8);
  return (
    <div className="space-y-2">
      <input value={q} onFocus={load} onChange={(e) => { setQ(e.target.value); load(); }} placeholder="Type the parent's name or email" className={`${inp} w-full`} data-testid="manual-payment-search" />
      {rows.map((p) => (
        <button key={p.id} onClick={() => onPick(p)} data-testid={`manual-payment-pick-${p.id}`}
          className="w-full text-left bg-slate-900 rounded-lg px-3 py-2 text-sm hover:bg-slate-700">
          <b>{p.name}</b> · {p.email} <span className="text-slate-400">· {p.children.map((c) => `${c.name} (G${c.grade})`).join(", ") || "no child yet"}</span>
        </button>
      ))}
      {list && q.trim().length > 1 && !rows.length && <p className="text-xs text-slate-400">No parent found — use "New parent".</p>}
    </div>
  );
}

function NewParent({ onPick }) {
  const [f, setF] = useState({ name: "", email: "", whatsapp: "" });
  const [kids, setKids] = useState([{ name: "", grade: 4 }]);
  const [busy, setBusy] = useState(false);
  const setKid = (i, k, v) => setKids(kids.map((c, j) => (j === i ? { ...c, [k]: v } : c)));
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/parents/quick", { ...f, children: kids.filter((c) => c.name.trim()) });
      toast.success(`Account created — ${data.parent.email} will receive a link to choose a password.`);
      onPick(data.parent);
    } catch (err) { toast.error(formatError(err.response?.data?.detail) || err.message); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="space-y-2" data-testid="manual-payment-new-parent">
      <div className="flex flex-wrap gap-2">
        <input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Parent name" className={`${inp} flex-1 min-w-[160px]`} data-testid="manual-new-name" />
        <input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Email" className={`${inp} flex-1 min-w-[200px]`} data-testid="manual-new-email" />
        <input value={f.whatsapp} onChange={(e) => setF({ ...f, whatsapp: e.target.value })} placeholder="WhatsApp (optional)" className={`${inp} w-44`} data-testid="manual-new-whatsapp" />
      </div>
      {kids.map((c, i) => (
        <div key={i} className="flex gap-2">
          <input required={i === 0} value={c.name} onChange={(e) => setKid(i, "name", e.target.value)} placeholder={`Child ${i + 1} name`} className={`${inp} flex-1`} data-testid={`manual-new-child-name-${i}`} />
          <select value={c.grade} onChange={(e) => setKid(i, "grade", Number(e.target.value))} className={inp} data-testid={`manual-new-child-grade-${i}`}>
            {[1, 2, 3, 4, 5, 6].map((g) => <option key={g} value={g}>Grade {g}</option>)}
          </select>
        </div>
      ))}
      <div className="flex gap-2">
        {kids.length < 6 && <button type="button" onClick={() => setKids([...kids, { name: "", grade: 4 }])} className="text-xs text-yellow-300 underline" data-testid="manual-new-add-child">+ another child</button>}
        <button disabled={busy} className="ml-auto inline-flex items-center gap-1 rounded-full bg-blue-600 px-4 py-1.5 text-sm font-semibold" data-testid="manual-new-create">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />} Create account & continue
        </button>
      </div>
    </form>
  );
}

function PaymentForm({ parent, onDone }) {
  const [sel, setSel] = useState({});
  const [prices, setPrices] = useState({});
  const [reason, setReason] = useState("");
  const [file, setFile] = useState(null);
  const [until, setUntil] = useState(nextMonth);
  const [busy, setBusy] = useState(false);
  const langs = (c) => (READING_OK(sel[c.id]?.plan) ? sel[c.id]?.reading || [] : []);
  const chosen = parent.children.filter((c) => sel[c.id]?.plan || langs(c).length);
  const planned = chosen.filter((c) => sel[c.id]?.plan);
  const listPrice = (c) => PLANS[sel[c.id].plan].price;
  const price = (c) => (prices[c.id] === undefined || prices[c.id] === "" ? listPrice(c) : Number(prices[c.id]));
  const total = planned.reduce((t, c) => t + price(c), 0) + chosen.reduce((t, c) => t + READING_PRICE * langs(c).length, 0);
  const discounted = planned.some((c) => price(c) < listPrice(c));
  const items = chosen.flatMap((c) => [
    ...(sel[c.id]?.plan ? [{ child_id: c.id, plan: sel[c.id].plan, subjects: sel[c.id].subjects || [] }] : []),
    ...(langs(c).length ? [{ child_id: c.id, plan: "reading", langs: langs(c) }] : []),
  ]);
  const send = async (confirm) => {
    if (!chosen.length) return toast.error("Choose a plan or the Reading kit for at least one child.");
    if (!file) return toast.error("The payment screenshot or PDF is required.");
    if (discounted && reason.trim().length < 3) return toast.error("Give a reason for the lower price.");
    const fd = new FormData();
    fd.append("parent_id", parent.id);
    fd.append("items", JSON.stringify(items));
    fd.append("file", file);
    fd.append("confirm", confirm ? "true" : "false");
    fd.append("valid_until", until);
    fd.append("prices", JSON.stringify(Object.fromEntries(planned.map((c) => [c.id, price(c)]))));
    fd.append("discount_reason", reason);
    setBusy(true);
    try {
      await api.post("/admin/billing/manual", fd);
      toast.success(confirm ? "Payment confirmed — access is active and the invoice is being emailed." : "Payment saved as pending.");
      onDone();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); } finally { setBusy(false); }
  };
  if (!parent.children.length) return <p className="text-sm text-amber-200">This parent has no child yet. Add a child in "Parents & Children" first.</p>;
  return (
    <div className="space-y-3" data-testid="manual-payment-form">
      {parent.children.map((c) => (
        <ChildRow key={c.id} child={c} value={sel[c.id] || {}} onChange={(v) => setSel({ ...sel, [c.id]: v })}
          price={prices[c.id]} listPrice={sel[c.id]?.plan ? listPrice(c) : 0} onPrice={(v) => setPrices({ ...prices, [c.id]: v })} />
      ))}
      {discounted && <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for the lower price (shown on the invoice)" className={`${inp} w-full border-amber-400`} data-testid="manual-payment-reason" />}
      <label className="flex items-center gap-2 rounded-xl border-2 border-dashed border-slate-500 p-3 cursor-pointer text-sm" data-testid="manual-payment-file-label">
        <Upload size={16} className="text-yellow-300" /> {file ? file.name : "Add the payment screenshot or PDF (required)"}
        <input type="file" accept="image/png,image/jpeg,image/webp,application/pdf" className="hidden" data-testid="manual-payment-file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
      </label>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-bold text-base mr-auto" data-testid="manual-payment-total">Total Rs {total}</span>
        <label className="text-xs text-slate-400">Access valid until</label>
        <input type="date" value={until} onChange={(e) => setUntil(e.target.value)} className={inp} data-testid="manual-payment-until" />
        <button disabled={busy} onClick={() => send(true)} className="inline-flex items-center gap-1 rounded-full bg-green-600 px-3 py-1.5 text-sm font-semibold" data-testid="manual-payment-confirm">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Confirm now
        </button>
        <button disabled={busy} onClick={() => send(false)} className="inline-flex items-center gap-1 rounded-full border-2 border-slate-500 px-3 py-1.5 text-sm font-semibold" data-testid="manual-payment-pending">
          <Clock size={14} /> Keep pending
        </button>
      </div>
    </div>
  );
}

function ChildRow({ child, value, onChange, price, listPrice, onPrice }) {
  const need = PLANS[value.plan]?.n || 0;
  const SUBJECTS = gradeSubjects(child.grade);
  const subs = value.plan === "all" ? SUBJECTS : value.subjects || [];
  const toggle = (s) => {
    if (subs.includes(s)) return onChange({ ...value, subjects: subs.filter((x) => x !== s) });
    if (subs.length >= need) return toast.error(`This plan includes ${need} subject(s).`);
    onChange({ ...value, subjects: [...subs, s] });
  };
  return (
    <div className="bg-slate-900 rounded-xl p-3 text-sm space-y-2" data-testid={`manual-child-${child.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <b className="mr-2">{child.name} (Grade {child.grade})</b>
        {Object.entries(PLANS).map(([id, p]) => (
          <button key={id} onClick={() => { onChange(value.plan === id ? { reading: value.reading } : { plan: id, subjects: [], reading: value.reading }); onPrice(""); }} data-testid={`manual-plan-${child.id}-${id}`}
            className={`rounded-full border px-3 py-1 text-xs font-semibold ${value.plan === id ? "bg-blue-600 border-blue-400" : "border-slate-600"}`}>{p.label} · Rs {p.price}</button>
        ))}
        {value.plan && <span className="ml-auto inline-flex items-center gap-1">Rs <input type="number" min={0} value={price ?? ""} placeholder={String(listPrice)} onChange={(e) => onPrice(e.target.value)} className={`${inp} w-20`} data-testid={`manual-price-${child.id}`} /></span>}
      </div>
      {value.plan && value.plan !== "all" && (
        <div className="flex flex-wrap gap-1.5">
          {SUBJECTS.map((s) => (
            <button key={s} onClick={() => toggle(s)} data-testid={`manual-subject-${child.id}-${s}`}
              className={`rounded-full border px-2.5 py-1 text-xs ${subs.includes(s) ? "bg-green-700 border-green-400" : "border-slate-600"}`}>{s}</button>
          ))}
          <span className="text-xs text-slate-400 self-center">{subs.length}/{need}</span>
        </div>
      )}
      <ReadingPick child={child} value={value} onChange={onChange} />
    </div>
  );
}

function ReadingPick({ child, value, onChange }) {
  if (!READING_OK(value.plan)) return <p className="text-xs text-sky-300" data-testid={`manual-reading-included-${child.id}`}>Reading kit included with this plan.</p>;
  const rd = value.reading || [];
  const toggle = (l) => onChange({ ...value, reading: rd.includes(l) ? rd.filter((x) => x !== l) : [...rd, l] });
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid={`manual-reading-${child.id}`}>
      <span className="text-xs text-slate-400">Reading kit · Rs {READING_PRICE}/language:</span>
      {["en", "fr"].map((l) => (
        <button key={l} onClick={() => toggle(l)} data-testid={`manual-reading-${child.id}-${l}`}
          className={`rounded-full border px-2.5 py-1 text-xs inline-flex items-center gap-1 ${rd.includes(l) ? "bg-sky-700 border-sky-400" : "border-slate-600"}`}>
          {rd.includes(l) && <Check size={11} />} {l === "en" ? "English" : "Français"}
        </button>
      ))}
    </div>
  );
}
